import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Box, Grid, IconButton, MenuItem, Select, Stack, Tooltip, Typography, Button } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  ChevronRight as ChevronRightIcon,
  Clear as ClearIcon,
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { getFileIcon } from 'assets/icons/fileTypes/fileIconMap';
import { FolderIcon } from 'assets/icons/FolderIcon';
import { isHipaaLink } from 'config/productProfile';

import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { Spinner, TwoOptionsDialog } from 'components';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { useApiRequests } from 'hooks/useApiRequests';
import { DocumentWithMetadataDto } from 'types/document';
import { Info } from 'components/Info';
import NoDocumentsOverlay from '../NoDocumentsOverlay/NoDocumentsOverlay';
import { MoreOptions } from '../MoreOptions/MoreOptions';
import { useDialog } from 'hooks';
import RenameFileDialog from 'components/Dialogs/RenameFileDialog/RenameFileDialog';
import FileIdDialog from 'components/Dialogs/FileIdDialog/FileIdDialog';
import { ADMIN, CLINIC_ADMIN } from 'constants/roles';
import FolderMoreOptions from './components/FolderMoreOptions/FolderMoreOptions';
import NewFolderDialog from 'components/Dialogs/NewFolderDialog/NewFolderDialog';
import ManageAccessModal from 'components/Dialogs/ManageAccessModal/ManageAccessModal';
import RefreshIcon from '@mui/icons-material/Refresh';
import { FolderAccessLevel } from 'types/document';
import { getIntlLocale } from 'utils';

import styles from './ClinicDocuments.module.scss';

const rowsPerPageOptions = [5, 10, 25, 50, 100];

const isSystemFile = (doc: DocumentWithMetadataDto) => {
  if (!doc?.path || typeof doc.path !== 'string') return false;
  const name = doc.path.split('/').pop()?.toLowerCase();
  return name === '.keep';
};

const isRootClinicPath = (path: string, clinicId?: string) => {
  if (!clinicId) return false;
  return path === `Clinic/${clinicId}`;
};

const formatBytes = (size: number | null | undefined): string => {
  if (!size || size <= 0) return '0 KB';
  if (size >= 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)} MB`;
  return `${(size / 1024).toFixed(1)} KB`;
};

type DocumentRow =
  | {
      kind: 'folder';
      id: string;
      name: string;
      createdAt: string | null;
      fileCount: number;
      totalSizeBytes: number;
    }
  | {
      kind: 'file';
      document: DocumentWithMetadataDto;
    };

type FolderRow = {
  kind: 'folder';
  id: string;
  name: string;
  createdAt: string | null;
  fileCount: number;
  totalSizeBytes: number;
};

export function ClinicDocuments() {
  const { t, i18n } = useTranslation('settings');
  const locale = getIntlLocale(i18n.resolvedLanguage || i18n.language || 'en');
  const {
    listPersonalDocumentsWithMetadata,
    listClinicDocuments,
    uploadClinicDocument,
    downloadPersonalDocument,
    updatePinnedState,
    renamePersonalDocument,
    deletePersonalDocument,
    createClinicFolder,
    renameClinicFolder,
    deleteClinicFolder,
    getFolderPermissions,
    getClinicStorage,
  } = useApiRequests();
  const user = useAppSelector(selectUser);
  const isAdmin: boolean = useMemo(() => {
    return user?.clinicUser?.roles?.some((role: string) => role === ADMIN || role === CLINIC_ADMIN) ?? false;
  }, [user]);

  const isMemberPersona: boolean = useMemo(() => user?.clinicUser?.via3UserType === 'Member', [user]);
  const isStaffPersona: boolean = useMemo(() => !isMemberPersona, [isMemberPersona]);

  const tableRef = useRef<HTMLDivElement>(null);
  const { showDialog } = useDialog();
  const { enqueueSnackbar } = useSnackbar();

  const [sortConfig, setSortConfig] = useState<{
    column: 'name' | 'date' | 'size' | null;
    order: 'asc' | 'desc' | 'none';
  }>({ column: 'date', order: 'desc' }); // default is Z-A date

  const [documents, setDocuments] = useState<DocumentWithMetadataDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [isDragging, setIsDragging] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [usedStorageMb, setUsedStorageMb] = useState<number>(0);
  const [selectedFilePaths, setSelectedFilePaths] = useState<Set<string>>(new Set());

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const suppressNextFolderOpenRef = useRef(false);

  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const [currentFolder, setCurrentFolder] = useState<FolderRow | null>(null);
  const basePath = user?.clinicId ? `Clinic/${user.clinicId}` : '';
  const [currentPath, setCurrentPath] = useState<string>(basePath);
  const [manageAccessFolder, setManageAccessFolder] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const [currentFolderAccess, setCurrentFolderAccess] = useState<FolderAccessLevel | null>(null);
  const [folderStatsByName, setFolderStatsByName] = useState<
    Map<string, { fileCount: number; totalSizeBytes: number }>
  >(new Map());
  const [allFolders, setAllFolders] = useState<FolderRow[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [folderFileIndex, setFolderFileIndex] = useState<Map<string, DocumentWithMetadataDto[]>>(new Map());

  const canRead = useMemo(() => {
    if (isAdmin || isStaffPersona) return true;

    if (!currentFolder) return false;

    return (
      currentFolderAccess === FolderAccessLevel.Read ||
      currentFolderAccess === FolderAccessLevel.Write ||
      currentFolderAccess === FolderAccessLevel.Full
    );
  }, [isAdmin, isStaffPersona, currentFolder, currentFolderAccess]);

  const canWrite = useMemo(() => {
    if (isAdmin || isStaffPersona) return true;

    // Members can NEVER write at root
    if (!currentFolder) return false;

    return currentFolderAccess === FolderAccessLevel.Write || currentFolderAccess === FolderAccessLevel.Full;
  }, [isAdmin, isStaffPersona, currentFolder, currentFolderAccess]);

  const openManageAccess = (folder: FolderRow) => {
    setManageAccessFolder({
      id: folder.id, // ✅ GUID
      name: folder.name,
    });
  };

  const closeManageAccess = () => {
    setManageAccessFolder(null);
  };

  const folderCount = useMemo(() => {
    return documents.filter((doc) => doc?.path && typeof doc.path === 'string' && doc.metadata === null).length;
  }, [documents]);

  const fileCount = useMemo(() => {
    return documents.filter(
      (doc) => doc?.path && typeof doc.path === 'string' && doc.metadata !== null && !isSystemFile(doc),
    ).length;
  }, [documents]);

  const fileRows: DocumentRow[] = useMemo(() => {
    const visibleDocs = documents.filter((doc) => {
      if (!doc?.path || typeof doc.path !== 'string') return false;
      if (!doc.metadata) return false; // folders come from metadata === null
      if (isSystemFile(doc)) return false;

      const normalized = doc.path.replace(/\/+$/, '');
      const segments = normalized.split('/');

      // Root files: Clinic/{clinicId}/file.ext
      if (!currentFolder && segments.length === 3) {
        return true;
      }

      // Files inside a folder
      if (currentFolder) {
        return normalized.toLowerCase().startsWith(`${basePath.toLowerCase()}/${currentFolder.name.toLowerCase()}/`);
      }

      return false;
    });

    return visibleDocs.map((doc) => ({
      kind: 'file',
      document: doc,
    }));
  }, [documents, currentFolder, basePath]);

  const derivedFolders = useMemo<FolderRow[]>(() => {
    const rootFolders = documents.filter((doc) => doc?.path && typeof doc.path === 'string' && doc.metadata === null);

    return rootFolders
      .map((doc) => {
        const parts = doc.path.replace(/\/+$/, '').split('/');
        const folderName = parts[2];
        const stats = folderStatsByName.get(folderName) ?? { fileCount: 0, totalSizeBytes: 0 };

        return {
          kind: 'folder' as const,
          id: doc.folderId ?? folderName.toLowerCase(),
          name: folderName,
          createdAt: null,
          fileCount: stats.fileCount,
          totalSizeBytes: stats.totalSizeBytes,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [documents, folderStatsByName]);

  useEffect(() => {
    if (!basePath || currentFolder) {
      setFolderStatsByName(new Map());
      return;
    }

    const rootFolders = documents.filter((doc) => doc?.path && typeof doc.path === 'string' && doc.metadata === null);

    if (!rootFolders.length) {
      setFolderStatsByName(new Map());
      return;
    }

    let isCancelled = false;

    const loadFolderStats = async () => {
      const statsMap = new Map<string, { fileCount: number; totalSizeBytes: number }>();
      const fileIndexMap = new Map<string, DocumentWithMetadataDto[]>();

      await Promise.all(
        rootFolders.map(async (folderDoc) => {
          const parts = folderDoc.path.replace(/\/+$/, '').split('/');
          const folderName = parts[2];
          const folderPath = `${basePath}/${folderName}`;

          try {
            const list = await listPersonalDocumentsWithMetadata(folderPath);
            const files = list.filter((doc) => doc?.metadata && !isSystemFile(doc));
            const totalSizeBytes = files.reduce((sum, doc) => sum + (doc.metadata?.size ?? 0), 0);

            statsMap.set(folderName, { fileCount: files.length, totalSizeBytes });
            fileIndexMap.set(folderName, files);
          } catch {
            statsMap.set(folderName, { fileCount: 0, totalSizeBytes: 0 });
            fileIndexMap.set(folderName, []);
          }
        }),
      );

      if (!isCancelled) {
        setFolderStatsByName(statsMap);
        setFolderFileIndex(fileIndexMap);
      }
    };

    void loadFolderStats();

    return () => {
      isCancelled = true;
    };
  }, [basePath, currentFolder, documents]);

  useEffect(() => {
    if (!currentFolder && derivedFolders.length > 0) {
      setAllFolders(derivedFolders);
    }
  }, [currentFolder, derivedFolders]);

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return null;

    if (currentFolder) {
      const files = fileRows.filter((r): r is Extract<DocumentRow, { kind: 'file' }> => {
        if (r.kind !== 'file') return false;
        const name = r.document.path.split('/').pop()?.toLowerCase() ?? '';
        return name.includes(q);
      });
      return { mode: 'folder' as const, files };
    }

    const folders = derivedFolders.filter((f) => f.name.toLowerCase().includes(q));

    const crossFiles: { folderName: string; document: DocumentWithMetadataDto }[] = [];
    folderFileIndex.forEach((docs, folderName) => {
      docs.forEach((doc) => {
        const name = doc.path.split('/').pop()?.toLowerCase() ?? '';
        if (name.includes(q)) crossFiles.push({ folderName, document: doc });
      });
    });
    fileRows.forEach((r) => {
      if (r.kind !== 'file') return;
      const name = r.document.path.split('/').pop()?.toLowerCase() ?? '';
      if (name.includes(q)) crossFiles.push({ folderName: '', document: r.document });
    });

    return { mode: 'root' as const, folders, files: crossFiles };
  }, [searchQuery, currentFolder, derivedFolders, fileRows, folderFileIndex]);

  const handleSort = (column: 'name' | 'date' | 'size') => {
    setSortConfig((prev) => {
      if (prev.column === column) {
        const nextOrder = prev.order === 'none' ? 'asc' : prev.order === 'asc' ? 'desc' : 'none';
        return { column, order: nextOrder };
      }
      return { column, order: 'asc' };
    });
  };

  const handleOpenFolder = async (folder: FolderRow) => {
    if (suppressNextFolderOpenRef.current) {
      suppressNextFolderOpenRef.current = false;
      return;
    }

    setCurrentFolder(folder);
    setCurrentPath(`${basePath}/${folder.name}`);

    if (isAdmin || isStaffPersona) {
      setCurrentFolderAccess(FolderAccessLevel.Full);
      return;
    }

    try {
      const perms = await getFolderPermissions(folder.id);
      const self = perms.find((p) => p.isCurrentUser);
      setCurrentFolderAccess(self?.accessLevel ?? FolderAccessLevel.NoAccess);
    } catch {
      setCurrentFolderAccess(FolderAccessLevel.NoAccess);
    }
  };

  const sortedRows = useMemo<DocumentRow[]>(() => {
    const folderRows = currentFolder ? [] : derivedFolders;

    const sortedFiles = fileRows
      .filter((row): row is Extract<DocumentRow, { kind: 'file' }> => row.kind === 'file')
      .sort((a, b) => {
        const aPinned = a.document.metadata?.pinned === true;
        const bPinned = b.document.metadata?.pinned === true;

        if (aPinned !== bPinned) {
          return aPinned ? -1 : 1;
        }

        const aName = typeof a.document.path === 'string' ? a.document.path.split('/').pop() ?? '' : '';

        const bName = typeof b.document.path === 'string' ? b.document.path.split('/').pop() ?? '' : '';

        return aName.localeCompare(bName);
      });

    return [...folderRows, ...sortedFiles];
  }, [derivedFolders, fileRows, currentFolder]);

  const pagedRows = sortedRows.slice(startIndex, endIndex);

  const paginationText = `${startIndex + 1}-${Math.min(endIndex, sortedRows.length)} ${t('documentsCommon.of')} ${
    sortedRows.length
  }`;

  useEffect(() => {
    setSelectedFilePaths((prev) => {
      const validPaths = new Set(
        documents.filter((doc) => doc.metadata !== null && !isSystemFile(doc)).map((doc) => doc.path),
      );
      const next = new Set(Array.from(prev).filter((path) => validPaths.has(path)));
      return next.size === prev.size ? prev : next;
    });
  }, [documents]);

  useEffect(() => {
    setSelectedFilePaths(new Set());
  }, [currentPath]);

  const handleDownload = async (fileName: string) => {
    if (!user?.clinicId || !user?.id) return;
    const path = currentFolder ? currentPath : basePath;

    try {
      await downloadPersonalDocument(path, fileName);
    } catch {
      enqueueSnackbar(t('documentsCommon.downloadFailed'), { variant: 'error' });
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [currentPath]);

  const handleRename = (fileName: string) => {
    const modal: { destroy: () => void } = showDialog(RenameFileDialog, {
      defaultFileName: fileName,
      onCancel: () => modal.destroy(),
      onConfirm: async (newName: string) => {
        modal.destroy();
        if (!user?.clinicId || !user?.id || !newName || newName === fileName) return;

        const path = currentPath;

        try {
          await renamePersonalDocument(path, fileName, newName);
          let list: DocumentWithMetadataDto[];

          if (isRootClinicPath(path, user?.clinicId)) {
            list = await listClinicDocuments(path);
          } else {
            list = await listPersonalDocumentsWithMetadata(path);
          }

          setDocuments(list);

          const totalBytes = list.reduce((sum, d) => sum + (d?.metadata?.size ?? 0), 0);
          setUsedStorageMb(Number((totalBytes / 1024 / 1024).toFixed(1)));
          enqueueSnackbar(t('documentsCommon.fileRenamedSuccessfully'), { variant: 'success' });
        } catch {
          enqueueSnackbar(t('documentsCommon.renameFailed'), { variant: 'error' });
        }
      },
    });
  };

  const handleRenameFolder = (folderName: string) => {
    const modal: { destroy: () => void } = showDialog(RenameFileDialog, {
      defaultFileName: folderName,
      onCancel: () => modal.destroy(),
      onConfirm: async (newName: string) => {
        modal.destroy();

        if (!newName.trim() || newName === folderName) return;

        const oldPath = `${basePath}/${folderName}`;
        const newPath = `${basePath}/${newName.trim()}`;

        try {
          await renameClinicFolder(oldPath, newPath);

          // Reset navigation (critical)
          setCurrentFolder(null);
          setCurrentPath(basePath);

          const list = await listClinicDocuments(basePath);
          setDocuments(list);

          enqueueSnackbar(t('clinicDocuments.folderRenamed'), { variant: 'success' });
        } catch {
          enqueueSnackbar(t('clinicDocuments.failedToRenameFolder'), { variant: 'error' });
        }
      },
    });
  };

  const handleFileId = async (fileName: string) => {
    if (!user?.clinicId || !user?.id) return;

    try {
      // 👇 Build the FULL blob path correctly
      const fullPath = currentFolder ? `${basePath}/${currentFolder.name}/${fileName}` : `${basePath}/${fileName}`;

      const modal: { destroy: () => void } = showDialog(FileIdDialog, {
        fileId: fullPath,
        onConfirm: (copiedId: string) => {
          modal.destroy();
          navigator.clipboard.writeText(copiedId);
          enqueueSnackbar(t('documentsCommon.fileIdCopiedToClipboard'), { variant: 'success' });
        },
        onCancel: () => modal.destroy(),
      });
    } catch {
      enqueueSnackbar(t('documentsCommon.failedToRetrieveFileId'), { variant: 'error' });
    }
  };

  const handleDelete = async (fileName: string) => {
    if (!user?.clinicId || !user?.id) return;

    const modal = showDialog(TwoOptionsDialog, {
      title: t('documentsCommon.deleteFileUpper'),
      text: (
        <Typography variant='body2' component='p'>
          {t('documentsCommon.deleteFileQuestion', { fileName })}
        </Typography>
      ),
      confirmText: t('documentsCommon.delete'),
      onConfirm: async () => {
        modal.destroy();

        const path = currentPath;

        try {
          await deletePersonalDocument(path, fileName);
          let list: DocumentWithMetadataDto[];

          if (isRootClinicPath(path, user?.clinicId)) {
            list = await listClinicDocuments(path);
          } else {
            list = await listPersonalDocumentsWithMetadata(path);
          }

          setDocuments(list);

          const totalBytes = list.reduce((sum, d) => sum + (d?.metadata?.size ?? 0), 0);
          setUsedStorageMb(Number((totalBytes / 1024 / 1024).toFixed(1)));
          enqueueSnackbar(t('documentsCommon.fileDeletedSuccessfully'), { variant: 'success' });
        } catch {
          enqueueSnackbar(t('documentsCommon.deleteFailed'), { variant: 'error' });
        }
      },
    });
  };

  const handleRowSelection = (docPath: string) => {
    setSelectedFilePaths((prev) => {
      const next = new Set(prev);
      if (next.has(docPath)) {
        next.delete(docPath);
      } else {
        next.add(docPath);
      }
      return next;
    });
  };

  const handleDeleteSelected = () => {
    if (!user?.clinicId || !user?.id || !canWrite || selectedFilePaths.size === 0) return;

    const path = currentPath;
    const selectedNames = Array.from(selectedFilePaths)
      .map((docPath) => docPath.split('/').pop())
      .filter((name): name is string => Boolean(name));

    if (selectedNames.length === 0) return;

    const modal = showDialog(TwoOptionsDialog, {
      title: t('documentsCommon.deleteFilesUpper'),
      text: (
        <Typography variant='body2' component='p'>
          {t('documentsCommon.deleteFilesQuestion')}
        </Typography>
      ),
      confirmText: t('documentsCommon.delete'),
      onConfirm: async () => {
        modal.destroy();
        setLoading(true);

        try {
          const results = await Promise.allSettled(
            selectedNames.map((fileName) => deletePersonalDocument(path, fileName)),
          );
          const failedCount = results.filter((result) => result.status === 'rejected').length;
          const deletedCount = results.length - failedCount;

          let list: DocumentWithMetadataDto[];
          if (isRootClinicPath(path, user?.clinicId)) {
            list = await listClinicDocuments(path);
          } else {
            list = await listPersonalDocumentsWithMetadata(path);
          }

          setDocuments(list);
          const totalBytes = list.reduce((sum, d) => sum + (d?.metadata?.size ?? 0), 0);
          setUsedStorageMb(Number((totalBytes / 1024 / 1024).toFixed(1)));
          setSelectedFilePaths(new Set());

          if (failedCount === 0) {
            enqueueSnackbar(t('documentsCommon.filesDeletedSuccessfully', { count: deletedCount }), {
              variant: 'success',
            });
          } else {
            enqueueSnackbar(t('documentsCommon.filesFailedToDelete', { count: failedCount }), {
              variant: 'error',
            });
          }
        } catch {
          enqueueSnackbar(t('documentsCommon.deleteFailed'), { variant: 'error' });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleDeleteFolder = (folderName: string) => {
    const modal = showDialog(TwoOptionsDialog, {
      title: t('clinicDocuments.deleteFolderUpper'),
      text: <Typography variant='body2'>{t('clinicDocuments.deleteFolderQuestion', { folderName })}</Typography>,
      confirmText: t('documentsCommon.delete'),
      onConfirm: async () => {
        modal.destroy();

        try {
          await deleteClinicFolder(`${basePath}/${folderName}`);

          const list = await listClinicDocuments(basePath);
          setDocuments(list);

          enqueueSnackbar(t('clinicDocuments.folderDeleted'), { variant: 'success' });
        } catch {
          enqueueSnackbar(t('clinicDocuments.failedToDeleteFolder'), { variant: 'error' });
        }
      },
    });
  };

  const handleMultipleFileUpload = async (files: File[]): Promise<void> => {
    if (!files.length || !user?.clinicId || !user?.id) return;

    const path = currentPath;
    setLoading(true);

    try {
      for (const file of files) {
        if (file.size > 25 * 1024 * 1024) {
          enqueueSnackbar(t('documentsCommon.fileTooLarge', { fileName: file.name }), { variant: 'error' });
          continue;
        }

        const alreadyExists = documents.some((doc) => doc.path.endsWith(file.name));
        const confirmOverwrite = alreadyExists
          ? window.confirm(t('documentsCommon.overwriteFileQuestion', { fileName: file.name }))
          : true;
        if (!confirmOverwrite) continue;

        try {
          await uploadClinicDocument(file, path, true);
          enqueueSnackbar(t('documentsCommon.uploadedFile', { fileName: file.name }), { variant: 'success' });
        } catch (err) {
          enqueueSnackbar(t('documentsCommon.failedToUploadFile', { fileName: file.name }), { variant: 'error' });
        }
      }

      let list: DocumentWithMetadataDto[];

      if (isRootClinicPath(path, user?.clinicId)) {
        list = await listClinicDocuments(path); // ✅ /api/documents
      } else {
        list = await listPersonalDocumentsWithMetadata(path); // ✅ /api/documents/full-list
      }

      setDocuments(list);

      const totalBytes = list.reduce((sum, d) => sum + (d?.metadata?.size ?? 0), 0);
      setUsedStorageMb(Number((totalBytes / 1024 / 1024).toFixed(1)));
    } catch (err) {
      enqueueSnackbar(t('documentsCommon.uploadError'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePinned = async (fileName: string) => {
    if (!user?.clinicId) return;

    const path = currentPath;
    const current = documents.find((doc) => doc.path.endsWith(fileName));
    const wasPinned = current?.metadata?.pinned === true;

    try {
      await updatePinnedState(path, fileName, !wasPinned);

      let list: DocumentWithMetadataDto[];

      if (isRootClinicPath(path, user?.clinicId)) {
        list = await listClinicDocuments(path); // ✅ ROOT
      } else {
        list = await listPersonalDocumentsWithMetadata(path); // ✅ NON-ROOT
      }

      setDocuments(list);

      const totalBytes = list.reduce((sum, d) => sum + (d?.metadata?.size ?? 0), 0);
      setUsedStorageMb(Number((totalBytes / 1024 / 1024).toFixed(1)));

      enqueueSnackbar(
        wasPinned ? t('documentsCommon.unpinnedFile', { fileName }) : t('documentsCommon.pinnedFile', { fileName }),
        { variant: 'success' },
      );
    } catch {
      enqueueSnackbar(t('documentsCommon.failedToUpdatePinnedState'), { variant: 'error' });
    }
  };

  const handleRowsPerPageChange = (event: SelectChangeEvent<string>) => {
    const newValue = parseInt(event.target.value, 10);
    setRowsPerPage(newValue);
    setCurrentPage(1);
  };

  const handleFirstPageClick = () => setCurrentPage(1);
  const handlePreviousPageClick = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPageClick = () => setCurrentPage((prev) => (endIndex < documents.length ? prev + 1 : prev));
  const handleLastPageClick = () => setCurrentPage(Math.ceil(documents.length / rowsPerPage));

  const loadDocuments = async () => {
    if (!user?.clinicId || !user?.id) return;

    const path = currentPath;
    try {
      setLoading(true);

      let list: DocumentWithMetadataDto[];

      if (isRootClinicPath(path, user.clinicId)) {
        list = await listClinicDocuments(path); // /api/documents
      } else {
        list = await listPersonalDocumentsWithMetadata(path); // /api/documents/full-list
      }

      setDocuments(list);

      if (isRootClinicPath(path, user.clinicId)) {
        try {
          const storage = await getClinicStorage();
          setUsedStorageMb(storage.megabytes);
        } catch {
          // silent fail — storage should never block UI
        }
      }
    } catch (err: any) {
      const status = err?.response?.status;

      // 403 = valid state: user has no shared folders
      if (status === 403) {
        setDocuments([]);
        setUsedStorageMb(0);
        return;
      }

      enqueueSnackbar(t('clinicDocuments.failedToRefreshDocuments'), { variant: 'error' });
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, [user?.clinicId, user?.id, currentPath]);

  const getArrow = (column: string) => {
    if (sortConfig.column !== column) return '';
    return sortConfig.order === 'asc' ? '▲' : sortConfig.order === 'desc' ? '▼' : '';
  };

  return (
    <div className={`${styles.container} ${loading && isInitialLoad ? styles.loadingInitial : ''}`}>
      <input
        type='file'
        ref={fileInputRef}
        style={{ display: 'none' }}
        multiple
        onChange={async (e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length === 0) return;

          await handleMultipleFileUpload(files);

          (e.target as HTMLInputElement).value = ''; // ✅ Reset input
        }}
      />

      <>
        {loading && (
          <div className={styles.overlay}>
            <Spinner small transparent />
          </div>
        )}

        {isInitialLoad ? null : documents.length === 0 ? (
          isAdmin || isStaffPersona || (currentFolder && canWrite) ? (
            <NoDocumentsOverlay onFileSelect={handleMultipleFileUpload} />
          ) : (
            <Box className={styles.notAvailable}>
              <Typography variant='body1'>
                {currentFolder
                  ? t('clinicDocuments.noFilesInFolder')
                  : t('clinicDocuments.noFoldersSharedContactAdmin')}
              </Typography>
            </Box>
          )
        ) : (
          <div className={styles.mainWrapper}>
            <div
              className={`${styles.dropZone} ${isDragging ? styles.dragActive : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                if (!canWrite) return;

                e.preventDefault();
                setIsDragging(false);

                const files = Array.from(e.dataTransfer.files ?? []);
                if (!files.length || !user?.clinicId || !user?.id) return;

                setLoading(true);
                const path = currentPath;

                (async () => {
                  try {
                    const uploadResults = await Promise.all(
                      files.map(async (file) => {
                        if (file.size > 25 * 1024 * 1024) {
                          enqueueSnackbar(t('documentsCommon.fileTooLarge', { fileName: file.name }), {
                            variant: 'error',
                          });
                          return false;
                        }

                        const filename = file.name;
                        const alreadyExists = documents.some((doc) => doc.path.endsWith(filename));
                        const confirmOverwrite = alreadyExists
                          ? window.confirm(t('documentsCommon.overwriteFileQuestion', { fileName: filename }))
                          : true;
                        if (!confirmOverwrite) return false;

                        try {
                          await uploadClinicDocument(file, path, true);
                          enqueueSnackbar(t('documentsCommon.uploadedFile', { fileName: filename }), {
                            variant: 'success',
                          });
                          return true;
                        } catch {
                          enqueueSnackbar(t('documentsCommon.failedToUploadFile', { fileName: filename }), {
                            variant: 'error',
                          });
                          return false;
                        }
                      }),
                    );

                    if (uploadResults.some(Boolean)) {
                      const list = isRootClinicPath(path, user?.clinicId)
                        ? await listClinicDocuments(path)
                        : await listPersonalDocumentsWithMetadata(path);

                      setDocuments(list);

                      const totalBytes = list.reduce((sum, d) => sum + (d?.metadata?.size ?? 0), 0);
                      setUsedStorageMb(Number((totalBytes / 1024 / 1024).toFixed(1)));
                    }
                  } finally {
                    setLoading(false);
                  }
                })();
              }}
            >
              <div className={styles.main}>
                {/* New Header */}
                <Stack className={styles.header} direction='row' justifyContent='space-between' alignItems='center'>
                  <Stack direction='row' alignItems='center' spacing={0.5}>
                    <Stack spacing={0.5}>
                      <Typography variant='body2'>
                        {currentFolder ? (
                          <>
                            <b>{fileCount}</b>{' '}
                            {t(fileCount === 1 ? 'clinicDocuments.fileSingular' : 'clinicDocuments.filePlural')}
                          </>
                        ) : folderCount === 0 ? (
                          <>
                            <b>{fileCount}</b> {t('clinicDocuments.totalFiles')}
                          </>
                        ) : (
                          <>
                            <b>{folderCount}</b>{' '}
                            {t(folderCount === 1 ? 'clinicDocuments.folderSingular' : 'clinicDocuments.folderPlural')}
                            &nbsp;&nbsp;
                            <b>{fileCount}</b>{' '}
                            {t(fileCount === 1 ? 'clinicDocuments.fileSingular' : 'clinicDocuments.filePlural')}
                          </>
                        )}
                      </Typography>
                      {!currentFolder && (
                        <Stack direction='row' alignItems='center' className={styles.storageInfoRow}>
                          <Typography variant='body2'>
                            <span className={styles.storageValue}>
                              <b>{usedStorageMb} MB</b> / <b>500 MB</b>
                            </span>
                          </Typography>
                          <Info text={t('clinicDocuments.clinicWideFilesInfo')} />
                        </Stack>
                      )}
                    </Stack>
                  </Stack>

                  <Stack direction='row' spacing={1} alignItems='center' className={styles.rightStack}>
                    <Box className={styles.searchWrapper}>
                      <SearchIcon className={styles.searchIcon} />
                      <input
                        className={styles.searchInput}
                        placeholder={t('clinicDocuments.search')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      <IconButton
                        size='small'
                        onClick={() => setSearchQuery('')}
                        className={styles.searchClear}
                        style={{ visibility: searchQuery ? 'visible' : 'hidden' }}
                      >
                        <ClearIcon fontSize='small' />
                      </IconButton>
                    </Box>

                    {canWrite && selectedFilePaths.size === 0 && (
                      <Button variant='contained' onClick={() => fileInputRef.current?.click()} disabled={loading}>
                        {t('documentsCommon.uploadFile')}
                      </Button>
                    )}

                    {isAdmin && !currentFolder && selectedFilePaths.size === 0 && (
                      <Button
                        variant='outlined'
                        onClick={() => {
                          const modal: { destroy: () => void } = showDialog(NewFolderDialog, {
                            defaultFileName: '',
                            onCancel: () => modal.destroy(),
                            onConfirm: async (name: string) => {
                              modal.destroy();

                              if (!name.trim() || !user?.clinicId) return;

                              try {
                                await createClinicFolder(name.trim());

                                const list = await listClinicDocuments(basePath);

                                setDocuments(list);

                                const totalBytes = list.reduce((sum, d) => sum + (d?.metadata?.size ?? 0), 0);
                                setUsedStorageMb(Number((totalBytes / 1024 / 1024).toFixed(1)));

                                enqueueSnackbar(t('clinicDocuments.folderCreated'), { variant: 'success' });
                              } catch {
                                enqueueSnackbar(t('clinicDocuments.failedToCreateFolder'), { variant: 'error' });
                              }
                            },
                          });
                        }}
                      >
                        {t('clinicDocuments.newFolder')}
                      </Button>
                    )}

                    <Tooltip title={t('clinicDocuments.refreshDocuments')}>
                      <span>
                        <IconButton size='small' onClick={loadDocuments} disabled={loading}>
                          <RefreshIcon className={`${styles.refreshIcon} ${loading ? styles.refreshing : ''}`} />
                        </IconButton>
                      </span>
                    </Tooltip>

                    {canWrite && selectedFilePaths.size > 0 && (
                      <Button variant='contained' color='error' sx={{ color: '#fff' }} onClick={handleDeleteSelected}>
                        {t('documentsCommon.delete')}
                      </Button>
                    )}
                  </Stack>
                </Stack>

                {currentFolder && (
                  <Stack
                    direction='row'
                    spacing={1}
                    alignItems='center'
                    sx={{
                      pt: '10px',
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant='body2'
                      sx={{ cursor: 'pointer', color: 'primary.main' }}
                      onClick={() => {
                        setCurrentFolder(null);
                        setCurrentPath(basePath);
                        setCurrentFolderAccess(null);
                      }}
                    >
                      {isHipaaLink
                        ? t('documentsSettings.backToClinicDocuments')
                        : t('documentsSettings.backToSharedDocuments')}
                    </Typography>

                    <ChevronRightIcon fontSize='small' />

                    <Select
                      value={currentFolder.name}
                      onChange={(e) => {
                        const selectedName = e.target.value as string;
                        const folder = allFolders.find((f) => f.name === selectedName);
                        if (folder) void handleOpenFolder(folder);
                      }}
                      variant='standard'
                      disableUnderline
                      sx={{
                        '& .MuiSelect-select': {
                          py: 0,
                          fontSize: '1.4rem',
                          fontWeight: 600,
                          color: 'var(--app-text-default)',
                        },
                        '& .MuiSelect-icon': {
                          color: 'var(--app-text-default)',
                          fontSize: '1.1rem',
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: { maxHeight: 480 },
                        },
                      }}
                    >
                      {allFolders.map((folder) => (
                        <MenuItem key={folder.id} value={folder.name}>
                          {folder.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Stack>
                )}

                <div className={styles.table} ref={tableRef}>
                  <Grid className={`${styles.tableHeader} ${currentFolder ? styles.noFilesColumn : ''}`}>
                    <Typography variant='subtitle2' component='span' />
                    <Typography variant='subtitle2' component='span'>
                      {t('documentsCommon.type')} {getArrow?.('type')}
                    </Typography>
                    <Typography
                      variant='subtitle2'
                      component='span'
                      className={styles.sortableHeader}
                      onClick={() => handleSort('name')}
                    >
                      {t('documentsCommon.fileName')} {getArrow?.('name')}
                    </Typography>
                    <Typography
                      variant='subtitle2'
                      component='span'
                      className={`${styles.dateCell} ${styles.sortableHeader}`}
                      onClick={() => handleSort('date')}
                    >
                      {t('documentsCommon.date')} {getArrow?.('date')}
                    </Typography>
                    {!currentFolder && (
                      <Typography variant='subtitle2' component='span' className={styles.filesCell}>
                        {t('clinicDocuments.files')}
                      </Typography>
                    )}
                    <Typography
                      variant='subtitle2'
                      component='span'
                      className={`${styles.sizeCell} ${styles.sortableHeader}`}
                      onClick={() => handleSort('size')}
                    >
                      {t('documentsCommon.size')} {getArrow?.('size')}
                    </Typography>
                    <Typography variant='subtitle2' component='span' />
                  </Grid>

                  <Box
                    sx={{
                      maxHeight: rowsPerPage > 10 ? 'calc(47px * 10)' : 'unset',
                      overflowY: rowsPerPage > 10 ? 'auto' : 'unset',
                      scrollbarGutter: 'stable',
                      pr: 1,
                    }}
                  >
                    {searchResults ? (
                      // ---------- SEARCH RESULTS ----------
                      <>
                        {searchResults.mode === 'root' && searchResults.folders.length > 0 && (
                          <>
                            <Box
                              sx={{
                                px: 2,
                                py: 0.5,
                                bgcolor: 'action.hover',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              <Typography
                                variant='caption'
                                fontWeight={600}
                                sx={{ fontSize: '1.4rem', color: 'var(--app-text-default)' }}
                              >
                                {t('clinicDocuments.searchFolders')}
                              </Typography>
                            </Box>
                            {searchResults.folders.map((row) => (
                              <Grid
                                key={`search-folder-${row.id}`}
                                className={styles.tableItem}
                                sx={{ cursor: 'pointer' }}
                                onClick={() => handleOpenFolder(row)}
                              >
                                <span />
                                <FolderIcon
                                  sx={{ width: { xs: 32, sm: 24 }, height: { xs: 32, sm: 24 }, color: '#FBC02D' }}
                                />
                                <Typography variant='body1' className={styles.fileName}>
                                  {row.name}
                                </Typography>
                                <Typography variant='body1' className={styles.dateCell}>
                                  {row.createdAt ? new Date(row.createdAt).toLocaleDateString(locale) : '—'}
                                </Typography>
                                <Typography variant='body1' className={styles.filesCell}>
                                  {row.fileCount}
                                </Typography>
                                <Typography variant='body1' className={styles.sizeCell}>
                                  <span className={styles.sizeValue}>{formatBytes(row.totalSizeBytes)}</span>
                                </Typography>
                                <span />
                              </Grid>
                            ))}
                          </>
                        )}

                        {searchResults.mode === 'root' && searchResults.files.length > 0 && (
                          <>
                            <Box
                              sx={{
                                px: 2,
                                py: 0.5,
                                bgcolor: 'action.hover',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              <Typography
                                variant='caption'
                                fontWeight={600}
                                sx={{ fontSize: '1.4rem', color: 'var(--app-text-default)' }}
                              >
                                {t('clinicDocuments.searchFiles')}
                              </Typography>
                            </Box>
                            {searchResults.files.map(({ folderName, document: doc }) => {
                              const fileName = doc.path.split('/').pop() ?? '';
                              const extension = fileName.split('.').pop()?.toLowerCase() || '';
                              const FileTypeIcon = getFileIcon(extension);
                              const uploadedAt = doc.metadata?.uploadedAt ?? null;
                              const size = doc.metadata?.size ?? null;
                              const fileFolderPath = folderName ? `${basePath}/${folderName}` : basePath;
                              return (
                                <Grid
                                  key={`search-file-${doc.path}`}
                                  className={`${styles.tableItem} ${styles.noFilesColumn}`}
                                >
                                  <span />
                                  <Tooltip title={extension.toUpperCase()} className={styles.type}>
                                    <span>
                                      <FileTypeIcon sx={{ fontSize: 24 }} />
                                    </span>
                                  </Tooltip>
                                  <Tooltip title={`${folderName ? `${folderName} / ` : ''}${fileName}`}>
                                    <Box className={styles.fileName} sx={{ overflow: 'hidden' }}>
                                      {folderName && (
                                        <Typography
                                          variant='caption'
                                          sx={{
                                            display: 'block',
                                            lineHeight: 1.2,
                                            fontSize: '1.4rem',
                                            color: 'var(--app-text-muted)',
                                          }}
                                          noWrap
                                        >
                                          {folderName}
                                        </Typography>
                                      )}
                                      <Typography variant='body1' noWrap>
                                        {fileName}
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                  <Typography variant='body1' className={styles.dateCell}>
                                    {uploadedAt ? new Date(uploadedAt).toLocaleDateString(locale) : '—'}
                                  </Typography>
                                  <Typography variant='body1' className={styles.sizeCell}>
                                    <span className={styles.sizeValue}>{size ? formatBytes(size) : '—'}</span>
                                  </Typography>
                                  <div onClick={(event) => event.stopPropagation()}>
                                    <MoreOptions
                                      item={doc}
                                      onDownload={async () => {
                                        try {
                                          await downloadPersonalDocument(fileFolderPath, fileName);
                                        } catch {
                                          enqueueSnackbar(t('documentsCommon.downloadFailed'), { variant: 'error' });
                                        }
                                      }}
                                      onFileId={() => {
                                        const modal: { destroy: () => void } = showDialog(FileIdDialog, {
                                          fileId: `${fileFolderPath}/${fileName}`,
                                          onConfirm: (copiedId: string) => {
                                            modal.destroy();
                                            navigator.clipboard.writeText(copiedId);
                                            enqueueSnackbar(t('documentsCommon.fileIdCopiedToClipboard'), {
                                              variant: 'success',
                                            });
                                          },
                                          onCancel: () => modal.destroy(),
                                        });
                                      }}
                                      onRename={() => {
                                        const modal: { destroy: () => void } = showDialog(RenameFileDialog, {
                                          defaultFileName: fileName,
                                          onCancel: () => modal.destroy(),
                                          onConfirm: async (newName: string) => {
                                            modal.destroy();
                                            if (!newName || newName === fileName) return;
                                            try {
                                              await renamePersonalDocument(fileFolderPath, fileName, newName);
                                              await loadDocuments();
                                              enqueueSnackbar(t('documentsCommon.fileRenamedSuccessfully'), {
                                                variant: 'success',
                                              });
                                            } catch {
                                              enqueueSnackbar(t('documentsCommon.renameFailed'), { variant: 'error' });
                                            }
                                          },
                                        });
                                      }}
                                      onDelete={() => {
                                        const modal = showDialog(TwoOptionsDialog, {
                                          title: t('documentsCommon.deleteFileUpper'),
                                          text: (
                                            <Typography variant='body2' component='p'>
                                              {t('documentsCommon.deleteFileQuestion', { fileName })}
                                            </Typography>
                                          ),
                                          confirmText: t('documentsCommon.delete'),
                                          onConfirm: async () => {
                                            modal.destroy();
                                            try {
                                              await deletePersonalDocument(fileFolderPath, fileName);
                                              await loadDocuments();
                                              enqueueSnackbar(t('documentsCommon.fileDeletedSuccessfully'), {
                                                variant: 'success',
                                              });
                                            } catch {
                                              enqueueSnackbar(t('documentsCommon.deleteFailed'), { variant: 'error' });
                                            }
                                          },
                                        });
                                      }}
                                      showRename={canWrite}
                                      showDelete={canWrite}
                                      className={styles.actions}
                                    />
                                  </div>
                                </Grid>
                              );
                            })}
                          </>
                        )}

                        {searchResults.mode === 'folder' &&
                          searchResults.files.map((row) => {
                            const doc = row.document;
                            if (!doc?.path || typeof doc.path !== 'string') return null;
                            const fileName = doc.path.split('/').pop() ?? '';
                            const extension = fileName.split('.').pop()?.toLowerCase() || '';
                            const FileTypeIcon = getFileIcon(extension);
                            const pinned = doc.metadata?.pinned === true;
                            const uploadedAt = doc.metadata?.uploadedAt ?? null;
                            const size = doc.metadata?.size ?? null;
                            return (
                              <Grid
                                key={doc.path}
                                className={`${styles.tableItem} ${styles.noFilesColumn} ${
                                  selectedFilePaths.has(doc.path) ? styles.selected : ''
                                }`}
                                onClick={() => handleRowSelection(doc.path)}
                              >
                                <IconButton
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    void handleTogglePinned(fileName);
                                  }}
                                  className={`${styles.starButton} ${styles.pin}`}
                                >
                                  {pinned ? (
                                    <StarIcon sx={{ width: 20, height: 20, color: '#FFC107' }} />
                                  ) : (
                                    <StarBorderIcon sx={{ width: 20, height: 20, color: 'var(--app-text-muted)' }} />
                                  )}
                                </IconButton>
                                <Tooltip title={extension.toUpperCase()} className={styles.type}>
                                  <span>
                                    <FileTypeIcon sx={{ fontSize: 24 }} />
                                  </span>
                                </Tooltip>
                                <Tooltip title={fileName}>
                                  <Typography variant='body1' className={styles.fileName} noWrap>
                                    {fileName}
                                  </Typography>
                                </Tooltip>
                                <Typography variant='body1' className={styles.dateCell}>
                                  {uploadedAt ? new Date(uploadedAt).toLocaleDateString(locale) : '—'}
                                </Typography>
                                <Typography variant='body1' className={styles.sizeCell}>
                                  <span className={styles.sizeValue}>{size ? formatBytes(size) : '—'}</span>
                                </Typography>
                                <div onClick={(event) => event.stopPropagation()}>
                                  <MoreOptions
                                    item={doc}
                                    onDownload={() => handleDownload(fileName)}
                                    onFileId={() => {
                                      if (!canRead) return;
                                      void handleFileId(fileName);
                                    }}
                                    onRename={() => {
                                      if (!canWrite) return;
                                      handleRename(fileName);
                                    }}
                                    onDelete={() => {
                                      if (!canWrite) return;
                                      void handleDelete(fileName);
                                    }}
                                    showRename={canWrite}
                                    showDelete={canWrite}
                                    className={styles.actions}
                                  />
                                </div>
                              </Grid>
                            );
                          })}

                        {((searchResults.mode === 'root' &&
                          searchResults.folders.length === 0 &&
                          searchResults.files.length === 0) ||
                          (searchResults.mode === 'folder' && searchResults.files.length === 0)) && (
                          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                            <Typography variant='body2' color='text.secondary'>
                              {t('clinicDocuments.noSearchResults')}
                            </Typography>
                          </Box>
                        )}
                      </>
                    ) : (
                      // ---------- NORMAL TABLE ROWS ----------
                      pagedRows.map((row) => {
                        // ---------- FOLDER ROW ----------
                        if (row.kind === 'folder') {
                          return (
                            <Grid
                              key={`folder-${row.id}`}
                              className={styles.tableItem}
                              sx={{ cursor: 'pointer' }}
                              onClick={() => handleOpenFolder(row)}
                            >
                              <span />
                              <FolderIcon
                                sx={{
                                  width: { xs: 32, sm: 24 },
                                  height: { xs: 32, sm: 24 },
                                  color: '#FBC02D',
                                }}
                              />

                              <Typography variant='body1' className={styles.fileName}>
                                {row.name}
                              </Typography>

                              <Typography variant='body1' className={styles.dateCell}>
                                {row.createdAt ? new Date(row.createdAt).toLocaleDateString(locale) : '—'}
                              </Typography>

                              <Typography variant='body1' className={styles.filesCell}>
                                {row.fileCount}
                              </Typography>

                              <Typography variant='body1' className={styles.sizeCell}>
                                <span className={styles.sizeValue}>{formatBytes(row.totalSizeBytes)}</span>
                              </Typography>

                              {isAdmin && (
                                <FolderMoreOptions
                                  className={styles.actions}
                                  onRename={() => handleRenameFolder(row.name)}
                                  onDelete={() => handleDeleteFolder(row.name)}
                                  onManageAccess={!isHipaaLink ? () => openManageAccess(row) : undefined}
                                  onDismissByClickAway={() => {
                                    suppressNextFolderOpenRef.current = true;
                                  }}
                                />
                              )}
                            </Grid>
                          );
                        }

                        // ---------- FILE ROW ----------
                        const doc = row.document;

                        if (!doc?.path || typeof doc.path !== 'string') return null;

                        const fileName = doc.path.split('/').pop() ?? '';
                        const extension = fileName.split('.').pop()?.toLowerCase() || '';
                        const FileTypeIcon = getFileIcon(extension);
                        const pinned = doc.metadata?.pinned === true;
                        const uploadedAt = doc.metadata?.uploadedAt ?? null;
                        const size = doc.metadata?.size ?? null;

                        return (
                          <Grid
                            key={doc.path}
                            className={`${styles.tableItem} ${currentFolder ? styles.noFilesColumn : ''} ${
                              selectedFilePaths.has(doc.path) ? styles.selected : ''
                            }`}
                            onClick={() => handleRowSelection(doc.path)}
                          >
                            <IconButton
                              onClick={(event) => {
                                event.stopPropagation();
                                void handleTogglePinned(fileName);
                              }}
                              className={`${styles.starButton} ${styles.pin}`}
                            >
                              {pinned ? (
                                <StarIcon sx={{ width: 20, height: 20, color: '#FFC107' }} />
                              ) : (
                                <StarBorderIcon sx={{ width: 20, height: 20, color: 'var(--app-text-muted)' }} />
                              )}
                            </IconButton>

                            <Tooltip title={extension.toUpperCase()} className={styles.type}>
                              <span>
                                <FileTypeIcon sx={{ fontSize: 24 }} />
                              </span>
                            </Tooltip>

                            <Tooltip title={fileName}>
                              <Typography variant='body1' className={styles.fileName} noWrap>
                                {fileName}
                              </Typography>
                            </Tooltip>

                            <Typography variant='body1' className={styles.dateCell}>
                              {uploadedAt ? new Date(uploadedAt).toLocaleDateString(locale) : '—'}
                            </Typography>

                            {!currentFolder && (
                              <Typography variant='body1' className={styles.filesCell}>
                                —
                              </Typography>
                            )}

                            <Typography variant='body1' className={styles.sizeCell}>
                              <span className={styles.sizeValue}>{size ? formatBytes(size) : '—'}</span>
                            </Typography>

                            <div onClick={(event) => event.stopPropagation()}>
                              <MoreOptions
                                item={doc}
                                onDownload={() => handleDownload(fileName)}
                                onFileId={() => {
                                  if (!canRead) return;
                                  void handleFileId(fileName);
                                }}
                                onRename={() => {
                                  if (!canWrite) return;
                                  handleRename(fileName);
                                }}
                                onDelete={() => {
                                  if (!canWrite) return;
                                  void handleDelete(fileName);
                                }}
                                showRename={canWrite}
                                showDelete={canWrite}
                                className={styles.actions}
                              />
                            </div>
                          </Grid>
                        );
                      })
                    )}
                  </Box>
                </div>
              </div>

              {!searchResults && (
                <Stack className={styles.paginationRow}>
                  <div className={styles.topRow}>
                    <Stack direction='row' alignItems='center' spacing={1} className={styles.rowsPerPage}>
                      <Typography variant='body1'>{t('documentsCommon.rowsPerPage')}</Typography>
                      <Select
                        className={styles.select}
                        variant='outlined'
                        value={rowsPerPage.toString()}
                        onChange={handleRowsPerPageChange}
                        IconComponent={ChevronRightIcon}
                      >
                        {rowsPerPageOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </Stack>
                  </div>

                  <Stack direction='row' alignItems='center' className={styles.arrowRow}>
                    <Typography variant='body1' className={styles.paginationText}>
                      {paginationText}
                    </Typography>
                    <IconButton onClick={handleFirstPageClick} className={styles.paginationIcon}>
                      <FirstPageIcon />
                    </IconButton>
                    <IconButton onClick={handlePreviousPageClick} className={styles.paginationIcon}>
                      <ArrowBackIcon />
                    </IconButton>
                    <IconButton onClick={handleNextPageClick} className={styles.paginationIcon}>
                      <ArrowForwardIcon />
                    </IconButton>
                    <IconButton onClick={handleLastPageClick} className={styles.paginationIcon}>
                      <LastPageIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              )}
            </div>
          </div>
        )}
      </>

      {!isHipaaLink && manageAccessFolder && (
        <ManageAccessModal
          folderId={manageAccessFolder.id}
          folderName={manageAccessFolder.name}
          onClose={closeManageAccess}
        />
      )}
    </div>
  );
}

export default ClinicDocuments;
